import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import { useMe } from '../auth/useMe';
import { ApiErrorNotice } from './ApiErrorNotice';
import { Badge } from './ui';

/**
 * Who you are, what you may do, and how to stop being it.
 *
 * Authority here is a number 1–7 that decides which controls are even
 * offered, so it belongs in the chrome rather than buried in a profile page —
 * an operator who cannot see their own level cannot tell a missing button from
 * a broken one. Sign-out lives beside it for the same reason: this is where
 * identity is on screen, so this is where an operator looks to change it.
 *
 * The two abnormal cases are surfaced here rather than per-screen, because
 * they affect everything at once:
 *
 *   `unresolved` — group membership did not map to a level at all. A
 *   configuration problem with the account, not a decision about the person,
 *   and the identity-side counterpart of a 403 `level-unreadable`.
 *
 *   `degraded` — the level could not be freshly confirmed against Okta, so it
 *   may be stale. Explicitly NOT a loss of access.
 */
export function OperatorChip() {
  const me = useMe();
  const queryClient = useQueryClient();

  /*
   * `POST /api/auth/logout` destroys server-side session state, not merely the
   * cookie, so the only honest way to end a session is to ask and then believe
   * the answer. Nothing about this is faked locally: a UI that hid the identity
   * on click would show a signed-out chrome over a session that is still live
   * on the server, which is a security problem rather than a cosmetic one.
   *
   * The contract declares 204, 429 and 503 (`shutting-down`) — so a logout can
   * genuinely fail, and both failures are worth retrying rather than ignoring.
   * `retry` is not set here: the shared client already refuses to retry
   * mutations automatically (see lib/query.ts), which is correct — a retry
   * belongs to the operator, in front of the error explaining why.
   */
  const logout = useMutation({
    mutationFn: async () => {
      await api.POST('/api/auth/logout');
    },
    onSuccess: () => {
      // Every cached read was fetched as someone who no longer exists. Clearing
      // is not tidiness: leaving it means the next render paints another
      // operator's account data over a dead session.
      queryClient.clear();
      /*
       * A full document load rather than a client-side navigation. The session
       * cookie is HttpOnly and the app's whole notion of "who am I" comes from
       * a request the server answers, so the only way to be sure the UI matches
       * the server is to start the app over and let it ask again — landing on
       * the sign-in prompt if there is nothing left to ask about.
       */
      window.location.assign('/');
    },
  });

  if (!me.isSuccess) return null;

  const { level, levelSource, degraded, displayName, email, oktaId } = me.data;
  const name = displayName ?? email ?? oktaId;

  return (
    <div className="operator">
      {levelSource === 'unresolved' ? (
        <Badge tone="bad">
          <span title="Your group membership did not resolve to an access level.">
            level unresolved · {name}
          </span>
        </Badge>
      ) : (
        <Badge tone={degraded ? 'warn' : 'info'}>
          <span
            title={
              degraded
                ? 'Your level could not be freshly confirmed against Okta and may be stale. Your access has not changed.'
                : `Level ${level}, ${levelSource === 'inherited' ? 'inherited from your org level' : 'granted by group'}`
            }
          >
            L{level} · {name}
            {degraded ? ' · stale' : ''}
          </span>
        </Badge>
      )}

      <button
        type="button"
        className="signout"
        disabled={logout.isPending}
        onClick={() => logout.mutate()}
      >
        {logout.isPending ? 'Signing out…' : 'Sign out'}
      </button>

      {/*
        * Anchored under the chip rather than dropped into the 44px header bar,
        * which has no room for it. It stays until dismissed: an operator who
        * believes they signed out and did not is the case this whole control
        * exists to get right, so the failure does not fade.
        */}
      {logout.isError && (
        <div className="operator-error">
          <ApiErrorNotice error={logout.error} onRetry={() => logout.mutate()} />
          <button type="button" onClick={() => logout.reset()}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
